import type { QueryClient } from "@tanstack/react-query";
import { projectKeys } from "queries";
import { getDocumentId, type ProjectData } from "types/project.types";
import {
  readCachedProjectDetail,
  unwrapProjectApiPayload,
} from "hooks/useProjects/projectDetailQuery";
import type { ProjectPayload } from "hooks/useProjects/useProjects.types";

type DraftScenesCacheEntry = {
  document?: { document_id?: string };
};

export type ResolveScreenplayDocumentIdInput = {
  queryClient: QueryClient;
  projectId: string;
  /** From useProjectIdentifiers (selected version or derived). */
  preferredDocumentId?: string | number | null;
  /** Transformed project from the detail query, when available. */
  project?: ProjectData | null;
};

const isUsableDocumentId = (value: string | number | null | undefined): value is string | number =>
  value != null && String(value) !== "" && String(value) !== "0";

/**
 * Resolves the screenplay document id for script routes when the detail query is still loading,
 * Redux selection is stale, or the React Query cache was populated by another subscriber.
 */
export const resolveScreenplayDocumentId = ({
  queryClient,
  projectId,
  preferredDocumentId,
  project,
}: ResolveScreenplayDocumentIdInput): string => {
  if (isUsableDocumentId(preferredDocumentId)) {
    return String(preferredDocumentId);
  }

  if (project) {
    const fromProject = getDocumentId(project);
    if (isUsableDocumentId(fromProject)) {
      return String(fromProject);
    }
  }

  const cachedPayload = readCachedProjectDetail(queryClient, projectId);
  if (cachedPayload) {
    const fromCache = getDocumentId(
      unwrapProjectApiPayload<ProjectPayload>(cachedPayload) as ProjectData
    );
    if (isUsableDocumentId(fromCache)) {
      return String(fromCache);
    }
  }

  const preferredFromSources = project
    ? getDocumentId(project)
    : cachedPayload
      ? getDocumentId(unwrapProjectApiPayload<ProjectPayload>(cachedPayload) as ProjectData)
      : undefined;
  const preferredStr = isUsableDocumentId(preferredFromSources)
    ? String(preferredFromSources)
    : null;

  const draftEntries = queryClient
    .getQueriesData<DraftScenesCacheEntry>({ queryKey: ["script", "draft-scenes"] })
    .filter(([key]) => key[2] === projectId);

  if (preferredStr) {
    for (const [, value] of draftEntries) {
      const docId = value?.document?.document_id;
      if (docId && String(docId) === preferredStr) {
        return String(docId);
      }
    }
  }

  for (const [, value] of draftEntries) {
    const docId = value?.document?.document_id;
    if (isUsableDocumentId(docId)) {
      return String(docId);
    }
  }

  const detailFromScriptKey = queryClient.getQueryData<ProjectPayload>(
    projectKeys.detail(projectId)
  );
  if (detailFromScriptKey) {
    const fromDetailKey = getDocumentId(
      unwrapProjectApiPayload<ProjectPayload>(detailFromScriptKey) as ProjectData
    );
    if (isUsableDocumentId(fromDetailKey)) {
      return String(fromDetailKey);
    }
  }

  return "";
};
